import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Mail, MapPin, Phone, Send, Shield } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const { t } = useLanguage();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success(t.contact.successMsg);
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
      });
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast.error(t.common.error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-20">
      {/* Hero Section */}
      <section className="py-24 gradient-bg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="section-label">{t.nav.contact}</span>
            <h1 
              data-testid="contact-title"
              className="font-serif text-4xl sm:text-5xl font-bold text-[#1A252C] mt-4 mb-6"
            >
              {t.contact.pageTitle}
            </h1>
            <p className="text-lg text-[#546A76] leading-relaxed">
              {t.contact.subtitle}
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Left - Contact Info */}
            <div>
              <h2 className="font-serif text-3xl font-bold text-[#1A252C] mb-8">
                {t.contact.infoTitle}
              </h2>

              {/* Reassurance Message */}
              <div className="flex items-start gap-4 p-6 rounded-2xl bg-[#4A708B]/5 border border-[#4A708B]/20 mb-10">
                <Shield className="w-6 h-6 text-[#4A708B] mt-1 flex-shrink-0" />
                <p className="text-[#546A76] leading-relaxed">
                  {t.contact.reassurance}
                </p>
              </div>

              {/* Contact Details */}
              <div className="space-y-6">
                <div 
                  data-testid="contact-email-info"
                  className="flex items-center gap-4 p-6 rounded-2xl bg-[#FAFAFA]"
                >
                  <div className="w-14 h-14 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-7 h-7 text-[#D4AF37]" />
                  </div>
                  <div>
                    <p className="text-sm text-[#546A76] mb-1">{t.contact.emailLabel}</p>
                    <a 
                      href="mailto:info@fondationenat.org" 
                      className="text-lg font-medium text-[#1A252C] hover:text-[#4A708B] transition-colors"
                    >
                      info@fondationenat.org
                    </a>
                  </div>
                </div>

                <div 
                  data-testid="contact-location-info"
                  className="flex items-center gap-4 p-6 rounded-2xl bg-[#FAFAFA]"
                >
                  <div className="w-14 h-14 rounded-xl bg-[#4A708B]/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-7 h-7 text-[#4A708B]" />
                  </div>
                  <div>
                    <p className="text-sm text-[#546A76] mb-1">{t.contact.locationLabel}</p>
                    <p className="text-lg font-medium text-[#1A252C]">
                      {t.contact.location}
                    </p>
                  </div>
                </div>
              </div>

              {/* Map Placeholder */}
              <div className="mt-10 rounded-3xl overflow-hidden h-[250px] bg-[#E2E8F0] flex items-center justify-center">
                <div className="text-center">
                  <MapPin className="w-12 h-12 text-[#4A708B] mx-auto mb-3" />
                  <p className="text-[#546A76] font-medium">Montréal, Québec</p>
                  <p className="text-[#546A76] text-sm">Canada</p>
                </div>
              </div>
            </div>

            {/* Right - Form */}
            <div>
              <div className="bg-[#FAFAFA] rounded-3xl p-8 sm:p-10">
                <h2 className="font-serif text-2xl font-bold text-[#1A252C] mb-8">
                  {t.contact.formTitle}
                </h2>

                <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="name">{t.contact.name} *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      data-testid="contact-name"
                      className="rounded-xl bg-white border-[#E2E8F0] focus:border-[#4A708B]"
                    />
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="email">{t.contact.email} *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        data-testid="contact-email"
                        className="rounded-xl bg-white border-[#E2E8F0] focus:border-[#4A708B]"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">{t.contact.phone}</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        data-testid="contact-phone"
                        className="rounded-xl bg-white border-[#E2E8F0] focus:border-[#4A708B]"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="subject">{t.contact.subject} *</Label>
                    <Input
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      data-testid="contact-subject"
                      className="rounded-xl bg-white border-[#E2E8F0] focus:border-[#4A708B]"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">{t.contact.message} *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      data-testid="contact-message"
                      className="rounded-xl bg-white border-[#E2E8F0] focus:border-[#4A708B]"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    data-testid="contact-submit"
                    className="w-full primary-btn rounded-full py-6 text-lg font-medium"
                  >
                    {isSubmitting ? t.common.loading : t.contact.submit}
                    {!isSubmitting && <Send className="ml-2 w-5 h-5" />}
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
